import React, { useState, useEffect } from "react";

interface CustomCalendarProps {
    selectedDate: Date | null;
    onDateChange: (date: Date) => void;
    onClose: () => void;
}

const CustomCalendar: React.FC<CustomCalendarProps> = ({
    selectedDate,
    onDateChange,
    onClose,
}) => {
    const today = new Date();
    const currentYear = today.getFullYear();
    const currentMonth = today.getMonth();

    const initialDate = selectedDate || new Date();
    const [currentDate, setCurrentDate] = useState<Date>(initialDate);
    const [selectedMonth, setSelectedMonth] = useState<number>(initialDate.getMonth());
    const [selectedYear, setSelectedYear] = useState<number>(initialDate.getFullYear());

    const months = [
        "Jan", "Feb", "Mar", "Apr", "May", "Jun",
        "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
    ];

    useEffect(() => {
        if (selectedDate) {
            setSelectedMonth(selectedDate.getMonth());
            setSelectedYear(selectedDate.getFullYear());
            setCurrentDate(selectedDate);
        }
    }, [selectedDate]);

    const handleMonthClick = (monthIndex: number) => {
        // Prevent selection of future months in the current year
        if (selectedYear === currentYear && monthIndex > currentMonth) {
            return;
        }
        setSelectedMonth(monthIndex);
    };

    const handleYearChange = (offset: number) => {
        setSelectedYear((prevYear) => {
            const newYear = prevYear + offset;
            return newYear > currentYear ? currentYear : newYear; // Restrict future years
        });
    };

    const handleConfirm = () => {
        const newDate = new Date(selectedYear, selectedMonth, 1);
        setCurrentDate(newDate);
        onDateChange(newDate);
        onClose();
    };

    return (
        <div className="custom-calendar-container">
            <div className="calendar-header d-flex justify-content-between align-items-center">
                <div className="selected-month-name display-6">
                    <span>{months[selectedMonth]}</span>
                </div>
                <div className="year-control">
                    <button
                        onClick={() => handleYearChange(-1)}
                        className="nav-btn"
                    >
                        &#9650;
                    </button>
                    <span className="selected-year">{selectedYear}</span>
                    <button
                        onClick={() => handleYearChange(1)}
                        className="nav-btn"
                        disabled={selectedYear >= currentYear} // Disable future years
                    >
                        &#9660;
                    </button>
                </div>
            </div>

            <div className="month-grid">
                {months.map((month, index) => (
                    <div
                        key={index}
                        className={`month-item ${
                            index === selectedMonth ? "selected-month" : ""
                        } ${selectedYear === currentYear && index > currentMonth ? "disabled" : ""}`}
                        onClick={() => handleMonthClick(index)}
                    >
                        {month}
                    </div>
                ))}
            </div>

            <div className="calendar-footer">
                <button className="cancel-btn" onClick={onClose}>
                    CANCEL
                </button>
                <button className="confirm-btn" onClick={handleConfirm}>
                    OK
                </button>
            </div>
        </div>
    );
};

export default CustomCalendar;
